<template>
  <div class="chart-wrapper-page">
    <div class="chart-card">
      <div class="chart-header">
        <h3 class="chart-title">Device Terminations</h3>
        <div class="filters">
          <div class="year-filters">
            <button
              v-for="opt in metricOptions"
              :key="opt.value"
              :class="['year-btn', { active: metric === opt.value }]"
              @click="setMetric(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
          <div class="year-filters">
            <button
              v-for="year in yearOptions"
              :key="year"
              :class="['year-btn', { active: selectedYear === year }]"
              @click="fetchData(year)"
            >
              {{ year }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="loading-state">
        <icon name="spinner" spin class="spinner"></icon>
        <p>Loading chart data...</p>
      </div>

      <template v-else>
        <div class="stat-row">
          <div class="stat-card">
            <span class="stat-label">Terminated devices</span>
            <span class="stat-value">{{ totals.terminated }}</span>
            <span class="stat-sub">Total in {{ selectedYear }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">With price</span>
            <span class="stat-value">{{ totals.withPrice }}</span>
            <span class="stat-sub">Terminated devices with a monthly price</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Total lost / month</span>
            <span class="stat-value">{{ fmtEur(totals.lostCents) }}</span>
            <span class="stat-sub">Sum of the monthly price of terminated devices</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Avg. lost per device</span>
            <span class="stat-value">{{ fmtEur(totals.avgCents) }}</span>
            <span class="stat-sub">Total lost ÷ devices with price</span>
          </div>
        </div>

        <div class="chart-container">
          <canvas ref="terminationChart"></canvas>
        </div>

        <div class="table-wrapper">
          <table class="stats-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Terminated</th>
                <th>With price</th>
                <th>Total lost</th>
                <th>Avg. lost</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in stats" :key="row.month" :class="{ empty: !row.terminated_count }">
                <td>{{ monthLabel(row.month) }}</td>
                <td>{{ row.terminated_count }}</td>
                <td>{{ row.with_price_count }}</td>
                <td>{{ fmtEur(row.total_lost_cents) }}</td>
                <td>{{ fmtEur(row.avg_lost_cents) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Chart from "chart.js";
import "vue-awesome/icons/spinner";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue")
  },
  data() {
    const thisYear = new Date().getFullYear();
    return {
      yearOptions: [thisYear - 2, thisYear - 1, thisYear],
      selectedYear: thisYear,
      metricOptions: [
        { value: "count", label: "Devices" },
        { value: "lost", label: "Lost €" }
      ],
      metric: "count",
      stats: [],
      isLoading: false,
      chart: null
    };
  },
  computed: {
    ...mapGetters(["getUserSystem"]),
    totals() {
      const sum = key => this.stats.reduce((acc, r) => acc + (r[key] || 0), 0);
      const withPrice = sum("with_price_count");
      const hasLost = this.stats.some(r => r.total_lost_cents != null);
      const lostCents = hasLost ? sum("total_lost_cents") : null;
      return {
        terminated: sum("terminated_count"),
        withPrice,
        lostCents,
        avgCents: lostCents != null && withPrice > 0 ? Math.round(lostCents / withPrice) : null
      };
    }
  },
  methods: {
    ...mapActions("axios_mod", ["simpleFetch"]),
    fetchData(year) {
      this.selectedYear = year;
      const data = {
        query: `
          query DeviceTerminationStats($year: Int!) {
            DeviceTerminationStats(year: $year) {
              month
              terminated_count
              with_price_count
              total_lost_cents
              avg_lost_cents
            }
          }
        `,
        variables: { year }
      };
      this.isLoading = true;
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) this.stats = res.DeviceTerminationStats;
        })
        .finally(() => {
          this.isLoading = false;
          this.$nextTick(this.renderChart);
        });
    },
    setMetric(metric) {
      this.metric = metric;
      this.renderChart();
    },
    renderChart() {
      if (!this.$refs.terminationChart) return;
      if (this.chart) this.chart.destroy();
      const isLost = this.metric === "lost";
      const axisColor = "#718096";
      const color = isLost ? "#dd6b20" : "#5469d4";
      const values = this.stats.map(r =>
        isLost ? (r.total_lost_cents == null ? null : r.total_lost_cents / 100) : r.terminated_count
      );
      this.chart = new Chart(this.$refs.terminationChart.getContext("2d"), {
        type: "bar",
        data: {
          labels: this.stats.map(r => this.monthLabel(r.month).substring(0, 3)),
          datasets: [{
            label: isLost ? "Total lost / month" : "Terminated devices",
            data: values,
            backgroundColor: color,
            hoverBackgroundColor: color,
            borderColor: "#fff",
            borderWidth: { left: 1, right: 1 },
            maxBarThickness: 40
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ gridLines: { display: false }, ticks: { fontColor: axisColor } }],
            yAxes: [{
              gridLines: { color: "#edf2f7", zeroLineColor: "#e2e8f0" },
              ticks: {
                beginAtZero: true,
                precision: 0,
                fontColor: axisColor,
                callback: v => (isLost ? "€" + v : v)
              }
            }]
          },
          tooltips: {
            mode: "index",
            intersect: false,
            callbacks: {
              title: items => (items.length ? this.monthLabel(this.stats[items[0].index].month) : ""),
              label: item => {
                const row = this.stats[item.index];
                return [
                  `Terminated: ${row.terminated_count}`,
                  `With price: ${row.with_price_count}`,
                  `Total lost: ${this.fmtEur(row.total_lost_cents)}`,
                  `Avg. lost: ${this.fmtEur(row.avg_lost_cents)}`
                ];
              }
            }
          }
        }
      });
    },
    monthLabel(month) {
      return `${MONTHS[parseInt(month.substring(5, 7), 10) - 1]} ${month.substring(0, 4)}`;
    },
    fmtEur(cents) {
      if (cents == null) return "—";
      return "€" + (cents / 100).toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  },
  watch: {
    getUserSystem() {
      this.fetchData(this.selectedYear);
    }
  },
  mounted() {
    this.fetchData(this.selectedYear);
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  }
};
</script>

<style scoped>
.chart-card {
  background: white;
  padding: 0;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;
}

.chart-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.filters {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.year-filters {
  display: flex;
  gap: 8px;
  background: #f7fafc;
  padding: 4px;
  border-radius: 8px;
}

.year-btn {
  padding: 8px 20px;
  border: none;
  background: transparent;
  color: #4a5568;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;
}

.year-btn:hover {
  background-color: #edf2f7;
}

.year-btn.active {
  background-color: #5469d4;
  color: white;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.stat-label {
  font-size: 13px;
  font-weight: 500;
  color: #718096;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #2d3748;
}

.stat-sub {
  font-size: 12px;
  color: #a0aec0;
}

.chart-container {
  height: 400px;
  position: relative;
}

.table-wrapper {
  margin-top: 24px;
  overflow-x: auto;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.stats-table th,
.stats-table td {
  padding: 8px 12px;
  text-align: right;
  white-space: nowrap;
  border-bottom: 1px solid #edf2f7;
}

.stats-table th:first-child,
.stats-table td:first-child {
  text-align: left;
}

.stats-table th {
  font-weight: 600;
  color: #4a5568;
  background: #f7fafc;
}

.stats-table td {
  color: #2d3748;
}

.stats-table tr.empty td {
  color: #a0aec0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #718096;
}

.loading-state .spinner {
  font-size: 32px;
  margin-bottom: 12px;
  color: #5469d4;
}

.loading-state p {
  margin: 0;
  font-size: 14px;
}

@media (max-width: 992px) {
  .stat-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .chart-header,
  .filters {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .year-btn {
    flex: 1;
    min-width: 0;
  }

  .chart-container {
    height: 300px;
  }
}
</style>
